import { useState } from 'react';
import { RUTAS } from '@/api/rutas';

import {
  AccountCircle,
  Dashboard as DashboardIcon,
  ExpandLess,
  ExpandMore,
  Logout,
  Menu as MenuIcon,
} from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DownloadIcon from '@mui/icons-material/Download';
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router';

import { DescargarExcel } from '@/components/DescargarExcel';
import { nombreDeMarca } from '@/lib/catalogo';
import { ingresoDesde } from '@/lib/sesion';
import { useDatosDelSitio } from '@/root';
import { esDelEquipo } from '@/tipos';

const SECCIONES = [
  // Va primera porque es la puerta al catálogo para quien no sabe el código de lo que busca.
  { nombre: 'Rubros', a: '/rubros' },
  { nombre: 'Marcas', a: '/marcas' },
  { nombre: 'Novedades', a: '/novedades' },
  { nombre: 'Sobre Nosotros', a: '/sobre-nosotros' },
  { nombre: 'Contáctenos', a: '/contactenos' },
];

export function Cabecera() {
  const { sesion, hayCatalogo, hayNovedades, marcasConPdf } = useDatosDelSitio();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const [cajonAbierto, setCajonAbierto] = useState(false);
  const [menuPdf, setMenuPdf] = useState<null | HTMLElement>(null);
  const [menuUsuario, setMenuUsuario] = useState<null | HTMLElement>(null);
  const [pdfEnCajon, setPdfEnCajon] = useState(false);
  const [excelAbierto, setExcelAbierto] = useState(false);
  const [saliendo, setSaliendo] = useState(false);

  // "Novedades" sólo aparece si hay algo que mostrar ahí. Un menú que lleva a una página vacía
  // es peor que un menú más corto.
  const secciones = SECCIONES.filter(s => s.nombre !== 'Novedades' || hayNovedades);

  const delEquipo = esDelEquipo(sesion);

  // El PDF y el Excel piden sesión. Sin ella los botones siguen a la vista —son a lo que se
  // viene—, pero llevan al ingreso y de vuelta a esta misma página, en vez de a un archivo que la
  // API no va a entregar. Rubros y Catálogo Digital no lo necesitan: sus páginas ya mandan solas.
  const aIngreso = ingresoDesde(pathname, search);

  async function cerrarSesion() {
    setSaliendo(true);
    setMenuUsuario(null);

    try {
      await fetch(RUTAS.sesion.salir, { method: 'POST', credentials: 'include' });
    } finally {
      setSaliendo(false);
      setCajonAbierto(false);

      // A la portada y recargando de verdad: la sesión la resuelve el servidor en cada pedido,
      // así que lo que hay que rehacer es el pedido, no el estado del navegador.
      navigate('/', { replace: true });
      navigate(0);
    }
  }

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={0}
      sx={{
        borderBottom: '1px solid rgba(255, 255, 255, 0.01)',
        zIndex: theme => theme.zIndex.drawer + 1,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ height: 70 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src="/navbar/logo-cking-navbar.webp"
              alt="CKING"
              width={130}
              height={45}
              sx={{ height: 'auto', width: 130, objectFit: 'contain', mr: '12px' }}
            />
          </Link>

          {/*
            Hasta `lg` y no hasta `md`. Entre 900 y 1200 tenían que entrar el logo, cinco
            secciones, tres botones de descarga y la cuenta, y no entraban: los rótulos se partían
            en dos renglones y la barra crecía al doble de alto. Abajo de `lg` las secciones se van
            al cajón —donde ya estaban— y los botones de descarga, que son a lo que se viene, se
            quedan a la vista.
          */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' } }}>
            <IconButton
              edge="start"
              onClick={() => setCajonAbierto(true)}
              aria-label="Abrir menu de navegacion"
              aria-expanded={cajonAbierto}
              sx={{
                color: 'white',
                '&:focus-visible': { outline: '2px solid white', outlineOffset: 2 },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'none', lg: 'flex' },
              justifyContent: 'center',
              minWidth: 0,
            }}
          >
            {secciones.map(seccion => (
              <Button
                key={seccion.nombre}
                component={Link}
                to={seccion.a}
                sx={{
                  my: '12px',
                  mx: '6px',
                  color: 'white',
                  display: 'block',
                  position: 'relative',
                  fontSize: '0.9rem',
                  whiteSpace: 'nowrap',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    width: 0,
                    height: '2px',
                    bottom: 0,
                    left: '50%',
                    backgroundColor: 'primary.main',
                    transition: 'all 0.3s ease-out',
                  },
                  '&:hover::after': { width: '100%', left: 0 },
                }}
              >
                {seccion.nombre}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: { md: '8px', lg: '12px' },
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {marcasConPdf.length > 0 && !sesion && (
              <Button
                component={Link}
                to={aIngreso}
                variant="outlined"
                color="primary"
                sx={{
                  fontWeight: 'bold',
                  borderRadius: 0,
                  px: '12px',
                  py: '6px',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                }}
              >
                Descargar Catálogo PDF
              </Button>
            )}

            {marcasConPdf.length > 0 && sesion && (
              <>
                <Button
                  id="boton-pdf"
                  variant="outlined"
                  color="primary"
                  onClick={evento => setMenuPdf(evento.currentTarget)}
                  aria-haspopup="true"
                  aria-expanded={Boolean(menuPdf)}
                  aria-controls={menuPdf ? 'menu-pdf' : undefined}
                  endIcon={<ArrowDropDownIcon />}
                  sx={{
                    fontWeight: 'bold',
                    borderRadius: 0,
                    px: '12px',
                    py: '6px',
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Descargar Catálogo PDF
                </Button>

                <Menu
                  id="menu-pdf"
                  disableScrollLock
                  anchorEl={menuPdf}
                  open={Boolean(menuPdf)}
                  onClose={() => setMenuPdf(null)}
                  slotProps={{ list: { 'aria-labelledby': 'boton-pdf' } }}
                >
                  <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
                    Ver en el navegador
                  </ListSubheader>

                  {marcasConPdf.map(marca => (
                    <MenuItem
                      key={marca}
                      component="a"
                      href={RUTAS.catalogo.pdf(marca)}
                      target="_blank"
                      onClick={() => setMenuPdf(null)}
                    >
                      Catálogo {nombreDeMarca(marca)}
                    </MenuItem>
                  ))}

                  <Divider />

                  {/* El visor del navegador trae su propio botón de descarga, pero no todos, y
                      en el teléfono casi ninguno. Esta es la manera de bajarlo sin depender de
                      eso. */}
                  <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
                    Descargar
                  </ListSubheader>

                  {marcasConPdf.map(marca => (
                    <MenuItem
                      key={`bajar-${marca}`}
                      component="a"
                      href={RUTAS.catalogo.pdf(marca) + '?descargar=1'}
                      onClick={() => setMenuPdf(null)}
                    >
                      <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
                      Catálogo {nombreDeMarca(marca)}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {hayCatalogo && (
              <Button
                {...(sesion
                  ? { onClick: () => setExcelAbierto(true) }
                  : { component: Link, to: aIngreso })}
                variant="outlined"
                color="primary"
                sx={{
                  fontWeight: 'bold',
                  borderRadius: 0,
                  px: '12px',
                  py: '6px',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {/* Con sesión es la lista de precios, y así se llama. Sin sesión el botón conserva
                    el nombre de siempre y lleva al ingreso. */}
                {sesion ? 'Descargar Lista de Precios' : 'Descargar Catálogo Excel'}
              </Button>
            )}

            {hayCatalogo && (
              <Button
                component={Link}
                to="/catalogo"
                variant="contained"
                color="primary"
                sx={{
                  fontWeight: 'bold',
                  borderRadius: 0,
                  px: '16px',
                  py: '6px',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                }}
              >
                Catálogo Digital
              </Button>
            )}

            {sesion ? (
              <>
                {delEquipo && (
                  <IconButton
                    component={Link}
                    to="/dashboard"
                    aria-label="Ir al panel de administracion"
                    sx={{
                      border: '1px solid white',
                      borderRadius: '4px',
                      p: 1,
                      color: 'white',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <DashboardIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                )}

                <IconButton
                  onClick={evento => setMenuUsuario(evento.currentTarget)}
                  aria-label="Menu de usuario"
                  aria-haspopup="true"
                  aria-expanded={Boolean(menuUsuario)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': {
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <AccountCircle sx={{ fontSize: 20 }} />
                </IconButton>
              </>
            ) : (
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                color="inherit"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 'bold',
                  borderRadius: '4px',
                  px: '16px',
                  py: '6px',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                  },
                }}
              >
                Iniciar Sesion
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>

      <Drawer
        variant="temporary"
        open={cajonAbierto}
        onClose={() => setCajonAbierto(false)}
        aria-label="Menu de navegacion principal"
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            backgroundColor: 'background.paper',
          },
        }}
      >
        <Box sx={{ textAlign: 'center', p: 2 }} role="navigation" aria-label="Navegacion principal">
          <Typography variant="h6" component="p" sx={{ my: 2, fontWeight: 700 }}>
            CKING
          </Typography>

          <List>
            {secciones.map(seccion => (
              <ListItem key={seccion.nombre} disablePadding>
                <ListItemButton
                  component={Link}
                  to={seccion.a}
                  sx={{ textAlign: 'center' }}
                  onClick={() => setCajonAbierto(false)}
                >
                  <ListItemText primary={seccion.nombre} />
                </ListItemButton>
              </ListItem>
            ))}

            {hayCatalogo && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/catalogo"
                  onClick={() => setCajonAbierto(false)}
                  sx={{
                    textAlign: 'center',
                    bgcolor: 'primary.main',
                    color: 'white',
                    my: 2,
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <ListItemText primary="Catálogo" />
                </ListItemButton>
              </ListItem>
            )}

            {marcasConPdf.length > 0 && !sesion && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to={aIngreso}
                  onClick={() => setCajonAbierto(false)}
                  sx={{
                    textAlign: 'center',
                    my: 1,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <ListItemText primary="Catálogo PDF" />
                </ListItemButton>
              </ListItem>
            )}

            {marcasConPdf.length > 0 && sesion && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => setPdfEnCajon(abierto => !abierto)}
                    sx={{
                      textAlign: 'center',
                      my: 1,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }}
                  >
                    <ListItemText primary="Catálogo PDF" />
                    {pdfEnCajon ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>

                <Collapse in={pdfEnCajon} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {marcasConPdf.map(marca => (
                      <ListItem key={marca} disablePadding>
                        <ListItemButton
                          component="a"
                          href={RUTAS.catalogo.pdf(marca)}
                          target="_blank"
                          sx={{ pl: 4, textAlign: 'left', bgcolor: 'background.paper' }}
                        >
                          <ListItemText primary={`Catálogo ${nombreDeMarca(marca)}`} />
                        </ListItemButton>

                        {/* En el teléfono el visor de PDF del navegador es el que peor anda, así
                            que la descarga va al lado y no escondida en otro menú. */}
                        <ListItemButton
                          component="a"
                          href={RUTAS.catalogo.pdf(marca) + '?descargar=1'}
                          aria-label={`Descargar el catálogo ${nombreDeMarca(marca)}`}
                          sx={{ flexGrow: 0, bgcolor: 'background.paper' }}
                        >
                          <DownloadIcon fontSize="small" />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            )}

            {hayCatalogo && (
              <ListItem disablePadding>
                <ListItemButton
                  {...(sesion ? {} : { component: Link, to: aIngreso })}
                  onClick={() => {
                    if (sesion) setExcelAbierto(true);
                    setCajonAbierto(false);
                  }}
                  sx={{
                    textAlign: 'center',
                    my: 1,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <ListItemText primary={sesion ? 'Lista de Precios Excel' : 'Catálogo en Excel'} />
                </ListItemButton>
              </ListItem>
            )}

            {sesion ? (
              <>
                {delEquipo && (
                  <ListItem disablePadding>
                    <ListItemButton
                      component={Link}
                      to="/dashboard"
                      sx={{
                        textAlign: 'center',
                        my: 1,
                        bgcolor: 'secondary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'secondary.dark' },
                      }}
                    >
                      <ListItemText primary="Panel" />
                    </ListItemButton>
                  </ListItem>
                )}

                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => void cerrarSesion()}
                    disabled={saliendo}
                    sx={{
                      textAlign: 'center',
                      my: 1,
                      mx: 1,
                      bgcolor: 'rgba(255, 0, 0, 0.1)',
                      color: 'rgba(255, 0, 0, 0.8)',
                      border: '1px solid rgba(255, 0, 0, 0.2)',
                      borderRadius: '8px',
                      '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.2)', color: 'rgba(255, 0, 0, 1)' },
                    }}
                  >
                    <Logout sx={{ mr: 1, fontSize: 18 }} />
                    <ListItemText primary="Cerrar Sesion" />
                  </ListItemButton>
                </ListItem>
              </>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/login"
                  onClick={() => setCajonAbierto(false)}
                  sx={{
                    textAlign: 'center',
                    my: 1,
                    mx: 1,
                    bgcolor: 'primary.main',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'primary.dark', color: 'white' },
                  }}
                >
                  <AccountCircle sx={{ mr: 1, fontSize: 18 }} />
                  <ListItemText primary="Iniciar Sesion" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>

      <Menu
        id="menu-usuario"
        disableScrollLock
        anchorEl={menuUsuario}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        open={Boolean(menuUsuario)}
        onClose={() => setMenuUsuario(null)}
      >
        <MenuItem disabled>
          <AccountCircle sx={{ mr: 1 }} />
          {sesion?.username ?? sesion?.email}
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => void cerrarSesion()} disabled={saliendo}>
          <Logout sx={{ mr: 1 }} />
          Cerrar Sesión
        </MenuItem>
      </Menu>

      <DescargarExcel
        abierto={excelAbierto}
        onCerrar={() => setExcelAbierto(false)}
        titulo={sesion ? 'Descargar Lista de Precios' : 'Descargar Catálogo Excel'}
      />
    </AppBar>
  );
}
